"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { motion, useReducedMotion } from "motion/react";
import { cn } from "../../lib/cn";
import { Badge } from "../../primitives/Badge";
import { transitions } from "../../lib/motion";

/**
 * SourceCard — a clickable tile in the "import from other sources" grid
 * (Paste Text, Webpage / GitHub URL, YouTube, Google Drive, Email, RSS, ...).
 *
 * Ported from Creator Studio's `vault/upload/SourceCard.tsx`. Every inline
 * navy hex (`#0A2540` borders / fills) and `blue-50` / `green-*` / `red-*`
 * wash is replaced with brand tokens (`border-border`, `bg-card`,
 * `text-primary`, `bg-primary`, `success`, `destructive`). The connection
 * badge keeps the donor's "Connected ✓ -> Disconnect on hover" CSS
 * `group-hover` cross-fade and the `stopPropagation` that keeps a badge click
 * from triggering the card's `onClick`.
 *
 * Three visual states via cva `state`:
 *   - `default`   — interactive, hover lifts the border + tints the surface.
 *   - `disabled`  — greyed, `cursor-not-allowed`, no click (the gated source
 *                   types: YouTube / Drive / Email / RSS until enabled).
 *   - `connected` — same as default but the surface carries a faint success
 *                   tint to read as "wired up".
 *
 * Motion: a subtle hover lift via `motion/react`, gated on
 * `useReducedMotion()` (reduced-motion users keep the color/border change,
 * which is not motion, but lose the translate). a11y: when interactive the
 * card is a `role="button"` with `tabIndex` and Enter/Space activation; the
 * connection badge is a real `<button>`.
 *
 * @example
 *   <SourceCard
 *     icon={<Globe className="size-5" />}
 *     title="Webpage / GitHub URL"
 *     description="Import content from webpages or GitHub repositories"
 *     onClick={() => setView("url")}
 *   />
 *
 *   <SourceCard
 *     icon={<HardDrive className="size-5" />}
 *     title="Google Drive"
 *     description="Connect and import from your Drive"
 *     connectionStatus="connected"
 *     onConnect={connect}
 *     onDisconnect={disconnect}
 *   />
 */
export const sourceCardVariants = cva(
  [
    "relative w-full rounded-lg border p-4 text-left transition-colors duration-180 ease-smooth-out",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
  ].join(" "),
  {
    variants: {
      state: {
        default: "border-border bg-card cursor-pointer hover:border-primary hover:bg-primary/5",
        disabled: "border-border bg-muted/40 cursor-not-allowed",
        connected: "border-success/40 bg-success/5 cursor-pointer hover:border-success",
      },
    },
    defaultVariants: {
      state: "default",
    },
  },
);

/** Whether a source shows no badge, a "Connect" CTA, or a "Connected" pill. */
export type SourceConnectionStatus = "none" | "connect" | "connected";

export interface SourceCardProps
  extends Omit<
      React.HTMLAttributes<HTMLDivElement>,
      "onClick" | "title" | "onDrag" | "onDragStart" | "onDragEnd" | "onAnimationStart"
    >,
    VariantProps<typeof sourceCardVariants> {
  /** Leading icon (typically a lucide icon sized `size-5`). */
  icon?: React.ReactNode;
  /** Tile title. */
  title: React.ReactNode;
  /** One-line description under the title. */
  description?: React.ReactNode;
  /** Disable the tile — greys it, removes click + hover, sets `aria-disabled`. */
  disabled?: boolean;
  /** Connection badge mode. `"connect"` shows a CTA; `"connected"` shows the swap pill. */
  connectionStatus?: SourceConnectionStatus;
  /** Card activation (click / Enter / Space). No-op when `disabled`. */
  onClick?: () => void;
  /** Connect-badge click. `stopPropagation` is handled internally. */
  onConnect?: (e: React.MouseEvent) => void;
  /** Disconnect-badge click (shown on hover over a connected pill). */
  onDisconnect?: (e: React.MouseEvent) => void;
  /** Optional extra content rendered under the description. */
  children?: React.ReactNode;
}

export const SourceCard = React.forwardRef<HTMLDivElement, SourceCardProps>(
  (
    {
      className,
      icon,
      title,
      description,
      disabled = false,
      connectionStatus = "none",
      onClick,
      onConnect,
      onDisconnect,
      children,
      state,
      ...rest
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    const resolvedState = state ?? (disabled ? "disabled" : connectionStatus === "connected" ? "connected" : "default");
    const interactive = !disabled && Boolean(onClick);

    const handleClick = () => {
      if (!disabled) onClick?.();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!interactive) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick?.();
      }
    };

    return (
      <motion.div
        ref={ref}
        role={interactive ? "button" : undefined}
        tabIndex={interactive ? 0 : undefined}
        aria-disabled={disabled || undefined}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        whileHover={interactive && !reduced ? { y: -2 } : undefined}
        transition={transitions.hover}
        className={cn(sourceCardVariants({ state: resolvedState }), className)}
        {...rest}
      >
        <div className="flex items-start gap-3">
          {icon ? (
            <span className={cn("mt-0.5 shrink-0", disabled ? "text-muted-foreground/60" : "text-primary")}>
              {icon}
            </span>
          ) : null}
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <h3 className={cn("truncate text-sm font-medium", disabled ? "text-muted-foreground" : "text-foreground")}>
                {title}
              </h3>

              {connectionStatus === "connect" && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onConnect?.(e);
                  }}
                  className={cn(
                    "shrink-0 rounded-full border border-primary bg-background px-3 py-1 text-xs font-medium text-primary shadow-sm transition-all",
                    "hover:bg-primary hover:text-primary-foreground hover:shadow-md active:scale-95",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ring-offset-background",
                  )}
                >
                  Connect
                </button>
              )}

              {connectionStatus === "connected" && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDisconnect?.(e);
                  }}
                  aria-label="Connected — click to disconnect"
                  className={cn(
                    "group/badge shrink-0 rounded-full border border-success/40 bg-success/15 px-3 py-1 text-xs font-medium text-success shadow-sm transition-all",
                    "hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive hover:shadow-md active:scale-95",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ring-offset-background",
                  )}
                >
                  <span className="inline-flex items-center gap-1 group-hover/badge:hidden">Connected ✓</span>
                  <span className="hidden group-hover/badge:inline">Disconnect</span>
                </button>
              )}
            </div>
            {description ? (
              <p className={cn("mt-1 text-xs", disabled ? "text-muted-foreground/70" : "text-muted-foreground")}>
                {description}
              </p>
            ) : null}
            {children}
          </div>
        </div>
        {disabled ? (
          <Badge variant="secondary" size="sm" className="absolute right-2 top-2">
            Soon
          </Badge>
        ) : null}
      </motion.div>
    );
  },
);
SourceCard.displayName = "SourceCard";
